import { Helper } from "../helpers/helper";
import { ImisPayloadFieldType, ImisResponse, PayloadFieldToTypeMapping } from "../models/imis.models";

export class PayloadUtility {
    public static parseImisPayload<T>(imisResponse: any): T {
        return this.parseResponseObject(imisResponse);
    }

    public static mapToImisPayload<T>(objectType: T, imisResponse: any, fieldToTypeMapping?: PayloadFieldToTypeMapping) {

        if (imisResponse.hasOwnProperty('Items')) {
            if (imisResponse.Items.hasOwnProperty('$values')) {

                let item = imisResponse.Items['$values'][0];
                this.assignPropertyValue(item, objectType, fieldToTypeMapping);
            }
        }
        else {
            // A single element was passed in
            let item = imisResponse;
            this.assignPropertyValue(item, objectType, fieldToTypeMapping);
        }

        return imisResponse;
    }

    /**
     * 
     * @param tableName data source name from imis
     * @param identity array of values that represent all the identity values associated with the data source
     * @param payload key-value pairings between data source field names and values
     * @param alternativeIdentityFieldNames (optional) if the field names are not the default `ID` as the primary and `Ordinal` as the secondary then this is where you would specify the alternative names
     * @param fieldToTypeMapping (optional) if the field does not have a standard mapping to a literal data type then the field can be specified here
     * @returns json object representing the properly formatted iMIS payload
     */
    public static getPanelSourcePayload(tableName: string, identity: any[], payload: any, alternativeIdentityFieldNames?: string[], fieldToTypeMapping?: PayloadFieldToTypeMapping) {
        let imisPayload = new ImisResponse();
        imisPayload.Properties = payload;

        let identityFieldNames = alternativeIdentityFieldNames || ["ID", "Ordinal"];
        let identityHasItems = Array.isArray(identity) && (identity.length > 0);
        if (identityHasItems && identity.length > 1) {
            imisPayload.Properties[identityFieldNames[0]] = identity[0].toString();
            imisPayload.Properties[identityFieldNames[1]] = Number.parseInt(identity[1]);
        } else if (identityHasItems) {
            imisPayload.Properties[identityFieldNames[0]] = Number.parseInt(identity[0]);
        }

        let imisPayloadJson = {
            "$type": "Asi.Soa.Core.DataContracts.GenericEntityData, Asi.Contracts",
            "EntityTypeName": tableName,
            "Properties": {
                "$type": "Asi.Soa.Core.DataContracts.GenericPropertyDataCollection, Asi.Contracts",
                "$values": []
            }
        };
        return this.mapToImisPayload(imisPayload, imisPayloadJson, fieldToTypeMapping);
    }

    public static createImisPayloadNameValuePair(name, value, fieldToTypeMapping?: PayloadFieldToTypeMapping) {
        let kindOf: string = typeof value;
        if (kindOf == "object" || kindOf == "undefined") {
            // default to string
            kindOf = ImisPayloadFieldType.STRING;
        }

        // check fieldToTypeMapping for custom types if any
        if (fieldToTypeMapping != null) {
            kindOf = <string>fieldToTypeMapping[name] || kindOf;
        }

        if (kindOf == ImisPayloadFieldType.STRING) {
            return {
                "$type": "Asi.Soa.Core.DataContracts.GenericPropertyData, Asi.Contracts",
                "Name": name,
                "Value": value
            };
        }
        else {
            let systemType = '';
            if (kindOf == ImisPayloadFieldType.BOOLEAN) {
                systemType = "System.Boolean";
            } else if (kindOf == ImisPayloadFieldType.NUMBER) {
                // could possibly be a integer or a float
                systemType = Number.isInteger(value) ? "System.Int32" : "System.Double";
            } else if (kindOf == ImisPayloadFieldType.BIGINT) {
                systemType = "System.Int64";
            } else if (kindOf == ImisPayloadFieldType.BINARY) {
                systemType = "System.Byte[], mscorlib";
            }

            return {
                "$type": "Asi.Soa.Core.DataContracts.GenericPropertyData, Asi.Contracts",
                "Name": name,
                "Value": {
                    "$type": systemType,
                    "$value": value
                }
            }
        }
    }

    private static parseResponseObject(responseObject: any) {
        let response = null;

        // each response object has a $type value, check that value to ensure you are dealing with a single object or a generic collection/dictionary
        // for the case you have an array, first check whether the percieved object is an array
        let isArray = Array.isArray(responseObject);
        let responseObjectItemsList = !isArray ? responseObject.$values : responseObject;
        let isSingleObjectOrGenericCollection = !isArray ? responseObject.$type == null || responseObject.$type.indexOf("GenericPropertyDataCollection") != -1 || responseObject.$type.indexOf("Dictionary") != -1 || responseObject.$type.indexOf("Collection") == -1 : false;
        if (isSingleObjectOrGenericCollection) {
            // not a collection, parse differently for a properties list
            response = {};
            if (responseObjectItemsList != null) {
                // properties list
                response = this.parsePropertiesList(responseObjectItemsList);
            } else {
                // standard object
                response = this.parseStandardObject(responseObject);
            }
        } else {
            // collection
            response = [];
            responseObjectItemsList.forEach((responseItem) => {
                // check if this is an object or a literal
                if (Helper.isObject(responseItem)) {
                    response.push(this.parseStandardObject(responseItem));
                } else {
                    response.push(responseItem);
                }
            });
        }

        return response;
    }

    private static parsePropertiesList(propertiesList) {
        let response = {};

        propertiesList.forEach((property) => {
            let value = "";

            if (property.Value != null && property.Value.$value != null) {
                value = property.Value.$value;
            } else {
                value = property.Value;
            }

            response[property.Name] = value;
        });

        return response;
    }

    private static parseStandardObject(responseObject) {
        let response = {};

        let objectKeys = Object.keys(responseObject);
        objectKeys.forEach((key) => {
            let value = responseObject[key];
            if (Helper.isObject(value)) {
                response[key] = this.parseResponseObject(value);
            } else {
                response[key] = value;
            }
        });

        return response;
    }

    private static assignPropertyValue(prop, viewModel, fieldToTypeMapping?: PayloadFieldToTypeMapping) {
        if (prop.hasOwnProperty('$type')
            && prop['$type'] == "Asi.Soa.Core.DataContracts.GenericPropertyDataCollection, Asi.Contracts") {
            // Name/Value pairs
            // looping over the viewModel first
            for (let key in viewModel) {
                // look for same element in the imisJSON object (prop['$values'])
                const foundInIMIS = prop['$values'].find(element => element["Name"] === key);
                if (foundInIMIS == undefined) {
                    // Try to insert the element in the IMIS object
                    let newNameValuePair = this.createImisPayloadNameValuePair(key, viewModel[key], fieldToTypeMapping);
                    if (newNameValuePair != null && newNameValuePair != undefined) {
                        prop['$values'].push(newNameValuePair);
                    }
                }
                else {
                    //assign the value
                    if (foundInIMIS.hasOwnProperty('Value') && foundInIMIS['Value'].hasOwnProperty('$value')) {
                        foundInIMIS['Value']['$value'] = viewModel[key];
                    }
                    else {
                        foundInIMIS['Value'] = viewModel[key];
                    }

                }
            }

        }
        else {
            // This is a normal property which is either a primitive type, like string or int, or another object
            // Check each sub property if it is an Object
            for (let subProp in prop) {
                if (prop[subProp] instanceof Object) {
                    // If it's an Object and it is specified by the viewModel, recursively look extract values until primitives are found.
                    if (subProp in viewModel) {
                        this.assignPropertyValue(prop[subProp], viewModel[subProp], fieldToTypeMapping);
                    }
                }
                else {
                    // If it is not an Object, simply set the value of the property
                    if (subProp in viewModel) {
                        prop[subProp] = viewModel[subProp];
                    }
                }
            }
        }
    }
}